import { ChangeEvent, useCallback, useMemo } from "react"
import { useField } from "formik"

import { RootState, selectTokenDecimals, useDappSelector } from "#/redux-state"
import { DATABASE_DECIMALS_COUNT } from "#/shared/constants"
import { SwapFormField, SwappableToken } from "#/shared/types"
import { bigIntToFloat, floatToBigInt } from "#/shared/utils"

import useSwapRate from "./useSwapRate"
import useSwapTokenBalance from "./useSwapTokenBalance"

const useSwapInput = (
  fieldName: SwapFormField,
  currentField: SwapFormField | null,
) => {
  const oppositeField = useMemo(
    () =>
      fieldName === SwapFormField.TOKEN_OUT
        ? SwapFormField.TOKEN_IN
        : SwapFormField.TOKEN_OUT,
    [fieldName],
  )

  const [, metaToken] = useField(`${fieldName}.token`)
  const [, metaAmount, helpersAmount] = useField(`${fieldName}.amount`)

  const [, oppositeFieldMetaAmount, oppositeFieldHelpersAmount] = useField(
    `${oppositeField}.amount`,
  )

  const [, oppositeFieldMetaToken] = useField(`${oppositeField}.token`)

  const isTokenInField = useMemo(
    () => fieldName === SwapFormField.TOKEN_IN,
    [fieldName],
  )

  const tokenIn = useMemo<SwappableToken>(
    () => (isTokenInField ? metaToken.value : oppositeFieldMetaToken.value),
    [isTokenInField, metaToken.value, oppositeFieldMetaToken.value],
  )

  const tokenOut = useMemo<SwappableToken>(
    () => (isTokenInField ? oppositeFieldMetaToken.value : metaToken.value),
    [isTokenInField, metaToken.value, oppositeFieldMetaToken.value],
  )

  const { getSwapAmountOut, getSwapAmountIn } = useSwapRate(tokenIn, tokenOut)

  const tokenInDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, tokenIn),
  )

  const tokenOutDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, tokenOut),
  )

  const { checkSwapTokenBalance } = useSwapTokenBalance({
    amount: isTokenInField ? metaAmount.value : oppositeFieldMetaAmount.value,
    tokenIn,
    tokenOut,
    fieldName,
  })

  const onInputBlur = useCallback(async () => {
    if (currentField !== fieldName || !metaAmount.value) return

    const [swapAmountIn, swapAmountOut] = isTokenInField
      ? await getSwapAmountOut(floatToBigInt(metaAmount.value, tokenInDecimals))
      : await getSwapAmountIn(floatToBigInt(metaAmount.value, tokenOutDecimals))

    const valueInToUpdate = bigIntToFloat(
      swapAmountIn,
      tokenInDecimals,
      tokenInDecimals,
    )

    const [integerPart, decimalsPart] = metaAmount.value.split(".")

    const truncatedDecimalsPart = decimalsPart
      ? decimalsPart.slice(0, tokenInDecimals)
      : ""

    const valueForCurrentInput = truncatedDecimalsPart
      ? `${integerPart}.${truncatedDecimalsPart}`
      : integerPart

    const valueOutToUpdate = bigIntToFloat(
      swapAmountOut,
      tokenOutDecimals,
      tokenOutDecimals,
    )

    if (isTokenInField) {
      // Calculating "out" amount returns the exact amount the user will receive
      // estimated by the contract so we don't need to adjust the "in" amount
      oppositeFieldHelpersAmount.setValue(
        valueOutToUpdate === "0" ? "" : valueOutToUpdate,
      )
    } else {
      // To calculate "in" amount we are performing the binary search to find the
      // best estimated amount the user needs to provide to get the desired "out"
      // amount. To avoid problems with slippage we are adjusting both inputs to
      // the best match returned by the contract.
      oppositeFieldHelpersAmount.setValue(
        valueInToUpdate === "0" ? "" : valueInToUpdate,
      )

      helpersAmount.setValue(
        valueForCurrentInput === "0" ? "" : valueForCurrentInput,
      )
    }
  }, [
    currentField,
    fieldName,
    isTokenInField,
    getSwapAmountOut,
    metaAmount.value,
    tokenInDecimals,
    tokenOutDecimals,
    oppositeFieldHelpersAmount,
    getSwapAmountIn,
    helpersAmount,
  ])

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = e.target
      const normalizedValue = value.trim()
      const isValidNumber = !Number.isNaN(+normalizedValue)
      const leadingZeroPattern = /^0\d+/

      if (normalizedValue === "") {
        helpersAmount.setValue("")
        helpersAmount.setError(undefined)
        return
      }

      const decimalsPart = normalizedValue.split(".")[1]

      const exceedsDecimals =
        !!decimalsPart && decimalsPart.length > DATABASE_DECIMALS_COUNT

      if (
        !isValidNumber ||
        leadingZeroPattern.test(normalizedValue) ||
        exceedsDecimals
      )
        return

      helpersAmount.setValue(normalizedValue)
      checkSwapTokenBalance()
    },
    [helpersAmount, checkSwapTokenBalance],
  )

  return {
    onInputChange,
    onInputBlur,
    token: metaToken.value,
    amount: metaAmount.value,
    error: isTokenInField ? metaAmount.error : undefined,
  }
}

export default useSwapInput
